/**
 * @internal true
 * @module briefing/BriefingTeam
 * Widget de Equipe Online — mostra membros da escola com status
 * derivado do último acesso. Clique no avatar abre chat direto.
 *
 * Fonte de dados: GET /admin/equipe (lista equipe da escola)
 * Presença: derivada de `ultimoAcesso` do usuário.
 */
import { useState, useEffect, useMemo } from 'react';
import { Users, MessageSquare, ChevronRight, Wifi, WifiOff } from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';

interface MembroEquipe {
  id: string;
  nome: string;
  email: string;
  cargo: string;
  perfil: string;
  fotoUrl?: string | null;
  ultimoAcesso?: string | null;
  ativo: boolean;
}

type StatusPresenca = 'online' | 'ocioso' | 'offline';

function getPresenca(ultimoAcesso?: string | null): StatusPresenca {
  if (!ultimoAcesso) return 'offline';
  const diff = Date.now() - new Date(ultimoAcesso).getTime();
  const mins = diff / 60000;
  if (mins < 5) return 'online';
  if (mins < 30) return 'ocioso';
  return 'offline';
}

function getPresencaColor(status: StatusPresenca): string {
  if (status === 'online') return '#22c55e';
  if (status === 'ocioso') return '#eab308';
  return '#94a3b8';
}

function getInitials(nome: string): string {
  const parts = nome.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0]?.[0]?.toUpperCase() || '?';
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min atrás`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h atrás`;
  const days = Math.floor(hrs / 24);
  return `${days}d atrás`;
}

export function BriefingTeam() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const escolaId = user?.escolaId || '';
  const [membros, setMembros] = useState<MembroEquipe[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!escolaId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const { data } = await api.get(`/escolas/${escolaId}/equipe`);
        const list = Array.isArray(data) ? data : (data as any)?.items || [];
        setMembros(list.filter((m: MembroEquipe) => m.ativo));
      } catch {
        setMembros([]);
      } finally {
        setLoading(false);
      }
    };
    load();

    // Refresh a cada 30s para atualizar presença
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [escolaId]);

  // Ordenar: online > ocioso > offline
  const sorted = useMemo(() => {
    const order: Record<StatusPresenca, number> = { online: 0, ocioso: 1, offline: 2 };
    return [...membros].sort((a, b) => {
      const sa = getPresenca(a.ultimoAcesso);
      const sb = getPresenca(b.ultimoAcesso);
      return order[sa] - order[sb];
    });
  }, [membros]);

  const onlineCount = membros.filter((m) => getPresenca(m.ultimoAcesso) === 'online').length;

  const handleOpenChat = (membro: MembroEquipe) => {
    window.dispatchEvent(
      new CustomEvent('ac:chat:open', {
        detail: {
          message: '',
          recipientId: membro.id,
          recipientName: membro.nome,
          preselect: 'operadores',
        },
      }),
    );
  };

  return (
    <div className="brief-card">
      <div className="brief-card-head">
        <h3 className="brief-card-title">
          <Users style={{ color: '#8b5cf6' }} />
          Equipe
          {onlineCount > 0 && (
            <span className="brief-card-badge brief-card-badge--green">{onlineCount} online</span>
          )}
        </h3>
        <button
          className="brief-card-link"
          onClick={() => navigate('/admin/equipe')}
        >
          Ver equipe <ChevronRight size={12} />
        </button>
      </div>
      <div className="brief-card-body">
        {loading ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="brief-skeleton" style={{ width: 44, height: 44, borderRadius: '50%' }} />
            ))}
          </div>
        ) : membros.length === 0 ? (
          <div className="brief-empty">
            <Users size={28} />
            <span className="brief-empty-text">Nenhum membro na equipe.</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
            {sorted.slice(0, 10).map((m) => {
              const status = getPresenca(m.ultimoAcesso);
              const statusColor = getPresencaColor(status);

              return (
                <div
                  key={m.id}
                  className="brief-alert-item"
                  onClick={() => handleOpenChat(m)}
                  title={`Enviar mensagem para ${m.nome}`}
                >
                  {/* Avatar */}
                  <div style={{ position: 'relative' }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: m.fotoUrl ? 'transparent' : `${statusColor}20`,
                      color: statusColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.72rem', fontWeight: 800,
                      overflow: 'hidden',
                      border: `2px solid ${statusColor}`,
                    }}>
                      {m.fotoUrl ? (
                        <img src={m.fotoUrl} alt={m.nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        getInitials(m.nome)
                      )}
                    </div>
                    {/* Status dot */}
                    <div style={{
                      position: 'absolute', bottom: -1, right: -1,
                      width: 10, height: 10, borderRadius: '50%',
                      background: statusColor,
                      border: '2px solid var(--bg-card, #fff)',
                    }} />
                  </div>

                  {/* Info */}
                  <div className="brief-alert-body">
                    <p className="brief-alert-title">{m.nome}</p>
                    <p className="brief-alert-sub">
                      {m.cargo || m.perfil}
                      {m.ultimoAcesso && status !== 'online' && (
                        <> · {timeAgo(m.ultimoAcesso)}</>
                      )}
                      {status === 'online' && <> · <span style={{ color: '#22c55e', fontWeight: 700 }}>Online</span></>}
                    </p>
                  </div>

                  {/* Chat button */}
                  <button
                    className="brief-alert-btn brief-alert-btn--share"
                    title={`Chat com ${m.nome}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenChat(m);
                    }}
                  >
                    <MessageSquare size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
